import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs, writeBatch, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

interface GeneralDonationDrawContextType {
  eligibleParticipants: string[];
  totalParticipants: number;
  isGeneralDonationActive: boolean;
  performGeneralDraw: (numberOfWinners: number, prizePerWinner: number) => Promise<Array<{
    userId: string;
    amount: number;
    timestamp: Date;
  }>>;
}

const GeneralDonationDrawContext = createContext<GeneralDonationDrawContextType | null>(null);

export function GeneralDonationDrawProvider({ children }: { children: React.ReactNode }) {
  const [eligibleParticipants, setEligibleParticipants] = useState<string[]>([]);
  const [isGeneralDonationActive, setIsGeneralDonationActive] = useState(false);

  useEffect(() => {
    const fetchEligibleParticipants = async () => {
      try {
        // Récupérer la donation active depuis settings/generalDonation
        const donationRef = doc(db, 'settings', 'generalDonation');
        const donationDoc = await getDoc(donationRef);
        
        if (!donationDoc.exists() || !donationDoc.data().isActive) {
          console.log('Pas de donation active trouvée');
          setIsGeneralDonationActive(false);
          setEligibleParticipants([]);
          return;
        }

        // Récupérer les clics
        const clicksRef = collection(db, 'generalDonationClicks');
        const clicksSnapshot = await getDocs(clicksRef);
        
        // Extraire les userId uniques des clics
        const uniqueParticipants = [...new Set(
          clicksSnapshot.docs.map(doc => doc.data().userId)
        )];

        console.log('Participants éligibles trouvés:', uniqueParticipants.length);
        setEligibleParticipants(uniqueParticipants);
        setIsGeneralDonationActive(uniqueParticipants.length > 0);
      } catch (error) {
        console.error('Erreur lors de la récupération des participants éligibles:', error);
      }
    };

    fetchEligibleParticipants();
  }, []);

  const performGeneralDraw = async (numberOfWinners: number, prizePerWinner: number) => {
    if (eligibleParticipants.length === 0) {
      throw new Error('Aucun participant éligible trouvé');
    }

    // Vérifier si le nombre de gagnants est valide
    if (numberOfWinners > eligibleParticipants.length) {
      throw new Error(`Le nombre de gagnants (${numberOfWinners}) ne peut pas être supérieur au nombre de participants éligibles (${eligibleParticipants.length})`);
    }

    const shuffled = [...eligibleParticipants].sort(() => Math.random() - 0.5);
    const winners = shuffled.slice(0, numberOfWinners).map(userId => ({
      userId,
      amount: prizePerWinner,
      timestamp: new Date()
    }));

    // Sauvegarder les résultats
    const batch = writeBatch(db);
    const drawRef = doc(collection(db, 'generalDonationDraws'));
    
    batch.set(drawRef, {
      winners,
      totalParticipants: eligibleParticipants.length,
      timestamp: new Date(),
      totalAmount: prizePerWinner * numberOfWinners
    });

    await batch.commit();

    return winners;
  };

  return (
    <GeneralDonationDrawContext.Provider value={{
      eligibleParticipants,
      totalParticipants: eligibleParticipants.length,
      isGeneralDonationActive,
      performGeneralDraw
    }}>
      {children}
    </GeneralDonationDrawContext.Provider>
  );
}

export const useGeneralDonationDraw = () => {
  const context = useContext(GeneralDonationDrawContext);
  if (!context) {
    throw new Error('useGeneralDonationDraw doit être utilisé à l\'intérieur d\'un GeneralDonationDrawProvider');
  }
  return context;
}; 